/**
 * ActionButtonsSection Component
 * Handles create, update, and cancel action buttons
 */

import React from 'react';
import { Button } from '@/components/ui/button';

interface ActionButtonsSectionProps {
  // State
  isLoading: boolean;
  isEditMode: boolean;
  isDuplicateMode: boolean;
  isUserEdited: boolean;
  createType: 'filters' | 'emrs' | '';
  selectedPatientsForGroup: number[];
  selectedEmrPatients: number[];
  
  // Actions
  onCreateGroup: () => void;
  onBack: () => void;
}

export const ActionButtonsSection: React.FC<ActionButtonsSectionProps> = ({
  isLoading,
  isEditMode,
  isDuplicateMode,
  isUserEdited,
  createType,
  selectedPatientsForGroup,
  selectedEmrPatients,
  onCreateGroup,
  onBack
}) => {
  const getButtonText = () => {
    if (isLoading) {
      return isEditMode ? 'Updating...' : 'Creating...';
    }
    
    if (isEditMode) {
      return 'Update Group';
    }
    
    if (isDuplicateMode && !isUserEdited) {
      return 'Duplicate Group';
    }
    
    return 'Create Group';
  };

  const isDisabled = () => {
    if (isLoading) return true;
    
    if (isEditMode) return false;
    
    // For create/duplicate mode, check if patients are selected
    const hasPatients = createType === 'filters' 
      ? selectedPatientsForGroup.length > 0
      : createType === 'emrs' 
        ? selectedEmrPatients.length > 0
        : false;
    
    return !hasPatients;
  };

  if (isEditMode) {
    return (
      <div className="flex space-x-4">
        <Button
          onClick={onCreateGroup}
          disabled={isLoading}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 font-medium rounded-lg"
        >
          {isLoading ? 'Updating...' : 'Update Group'}
        </Button>
        <Button
          onClick={onBack}
          className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-3 font-medium rounded-lg"
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex justify-center space-x-4 pt-6">
      <Button
        onClick={onCreateGroup}
        disabled={isDisabled()}
        className="bg-purple-200 hover:bg-purple-300 text-purple-800 px-12 py-3 font-medium disabled:opacity-50"
      >
        {getButtonText()}
      </Button>
      <Button
        onClick={onBack}
        variant="outline"
        className="px-12 py-3 border-pink-300 text-pink-600 hover:bg-pink-50 font-medium"
      >
        Cancel
      </Button>
    </div>
  );
};